import { useState } from 'react';
import { useLoaderData } from 'react-router';

import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    CONTENT_CAP_PADDED,
    DetailTileCard,
    LoadMoreButton,
    SectionHead,
    type DetailTile,
} from '~/ui-rocco/pages/detail-shared';

/** Two whole rows of the four-up grid, and two more each time it is asked. */
const PAGE_SIZE = 8;

/**
 * More projects from {professional} — the practice's other work, under the
 * same head the product page gives the supplier's catalogue.
 *
 * Replaces the carousel this block used to be. A carousel hides most of what
 * it holds behind a drag, and the row it sat in was doing a section title's
 * work in one line; the grid states how much there is and the head names it.
 */
export const MoreProjectsFromSection = () => {
    const { project, featuredProjects } = useLoaderData<typeof loader>();
    const [shown, setShown] = useState(PAGE_SIZE);

    const professional = project.Professional;
    const projects = featuredProjects?.FeaturedProjects?.projects ?? [];

    if (!professional?.Title || projects.length === 0) return null;

    const tiles = projects.map(
        (item) =>
            ({
                ...item,
                pinType: 'ProjectPin',
                professional: {
                    id: professional.ID,
                    name: professional.Title,
                },
                professionalLink: professional.Link,
                website: professional.Website,
            }) as DetailTile,
    );
    const visible = tiles.slice(0, shown);

    return (
        <section className="max-md:px-6 md:px-[3rem]">
            <div className={CONTENT_CAP_PADDED}>
                <SectionHead
                    tone="dark"
                    lead="More projects from"
                    subject={professional.Title}
                    subjectLink={professional.Link}
                    standfirst={`Other work by ${professional.Title}, across every category they build in.`}
                />

                <div className="grid grid-cols-2 max-md:mt-6 max-md:gap-x-3.5 max-md:gap-y-[3.6875rem] md:mt-[3.75rem] md:grid-cols-4 md:gap-x-3 md:gap-y-[6.25rem]">
                    {visible.map((tile) => (
                        <DetailTileCard
                            key={tile.id}
                            tile={tile}
                            theme="dark"
                            mediaClassName="md:aspect-[37/27]"
                            textClassName="md:text-[1rem] md:leading-[1.3]"
                        />
                    ))}
                </div>

                {shown < tiles.length ? (
                    <LoadMoreButton
                        tone="dark"
                        className="max-md:mt-10 md:mt-[3.75rem]"
                        onClick={() =>
                            setShown((current) =>
                                Math.min(tiles.length, current + PAGE_SIZE),
                            )
                        }
                    />
                ) : null}
            </div>
        </section>
    );
};
